import { Validators } from '@angular/forms'
import { MultiMediaMarkdownEditorComponent } from '@engineering11/multimedia-web'
import {
  e11FieldInputDynamicComponent,
  e11FieldInputNumber,
  e11FieldInputSelect,
  e11FieldInputSwitch,
  e11FieldInputTextArea,
  e11FormEventHelper,
  e11FormFor,
  E11FormForType,
  e11FormLayoutFullWidth,
  e11FormLayoutInlineGrid,
  e11LayoutGroup,
  IFormTemplateSpec,
  IFormTemplateSpecPosition,
} from '@engineering11/ui-lib/e11-form-builder'
import { IEditorMessage, IMarkdownFile } from '@engineering11/ui-lib/e11-markdown-editor'
import { ISelectOption } from '@engineering11/ui-lib/e11-select'
import { APPLICATION_STATE, INPUTS_MAXLENGTH_BY_CONTEXT, InputsContext, MessagingSystemUserIds } from 'shared-lib'
import { JOBS_AUTOMATION_MARKDOWN_CONFIG_OPTIONS, JOBS_AUTOMATION_MULTIMEDIA_CONFIG_OPTIONS } from './automation.constants'
import { delayUnitSelectOptions, ISendMessageFormData, ISendMessageSwimlaneFormData } from './job-automation.model'

export interface IAutomationFormConfig {
  senderOptions: ISelectOption[]
  canEditMessages: boolean
  togglePosition?: 'top' | 'bottom'
  showStandaloneToggle?: boolean
}

function sendMessageToggleLayout(defaults: Partial<ISendMessageFormData>, showStandaloneToggle?: boolean) {
  return e11FormLayoutFullWidth(
    [
      e11FieldInputSwitch('enabled', 'Enabled', defaults.enabled, {
        validatorOrOpts: [],
        hideInputError: true,
      }),
    ],
    {
      templateSpec: [
        {
          templateKey: 'messageFootNoteTemplate',
          templatePosition: 'bottom',
        },
      ],
      containerClass: !showStandaloneToggle ? 'e11-hidden' : '',
    }
  )
}

type OnFileUploadFn = (arg: IMarkdownFile[]) => void
type onFileRemoveFn = (arg: IMarkdownFile) => void

export type MultiMediaArgs = {
  onFileUpload: OnFileUploadFn
  onFileRemove: onFileRemoveFn
}

type SendMessageFormDataForBuilder = Omit<ISendMessageFormData, 'attachments'>
type SendMessageSwimlaneFormDataForBuilder = Omit<ISendMessageSwimlaneFormData, 'attachments'>

function textMessageForm(defaults: Partial<SendMessageFormDataForBuilder>, config: IAutomationFormConfig, multimediaArgs?: Partial<MultiMediaArgs>) {
  const { canEditMessages } = config

  const upgradeProductTemplate = canEditMessages ? [] : createTemplateSpec('upgradeProductTemplate')
  const resetFormTemplateSpec = canEditMessages ? createTemplateSpec('resetFormTemplate') : []
  const videoUrlPreviewTemplateSpec = createTemplateSpec('videoUrlPreviewTemplate')

  return e11LayoutGroup(
    [
      {
        fields: [],
        layouts: [
          {
            fields: [getMessageControl(defaults, config, multimediaArgs)],
            templateSpec: [...videoUrlPreviewTemplateSpec, ...resetFormTemplateSpec, ...upgradeProductTemplate],
          },
        ],
      },
    ],
    {}
  )
}

function delayControlForm(defaults: Partial<SendMessageFormDataForBuilder>, config: IAutomationFormConfig) {
  const { canEditMessages, senderOptions } = config

  return [
    e11FormLayoutInlineGrid(
      [
        e11FieldInputNumber('delayDuration', 'Delay time', defaults.delayDuration, {
          validatorOrOpts: [Validators.required, Validators.min(0)],
          hideInputError: true,
          isDisabled: !canEditMessages,
        }),
        e11FieldInputSelect('delayUnits', 'Units', delayUnitSelectOptions, defaults.delayUnits, {
          validatorOrOpts: [Validators.required],
          hideInputError: true,
          isDisabled: !canEditMessages,
        }),
      ],
      {
        fieldsClass: 'e11-w-1/2',
        containerClass: 'e11-w-full e11-mb-4',
      }
    ),
    e11FormLayoutFullWidth(
      [
        e11FieldInputSelect('senderId', 'This message is from', senderOptions, defaults.senderId, {
          hideInputError: true,
          isDisabled: !canEditMessages,
          validatorOrOpts: [Validators.required],
        }),
      ],
      {
        fieldsClass: 'e11-w-2/3',
        containerClass: 'e11-w-full e11-mb-4',
      }
    ),
  ]
}

export function swimlaneForm(swimlaneOptions: ISelectOption<APPLICATION_STATE | null>[], defaultSwimlane?: APPLICATION_STATE) {
  return e11LayoutGroup(
    [
      e11FormLayoutFullWidth(
        [
          e11FieldInputSelect('newSwimlane', 'New swimlane', swimlaneOptions, defaultSwimlane, {
            hideInputError: true,
            validatorOrOpts: [],
            extraInputOption: {
              required: true,
            },
          }),
        ],
        {
          fieldsClass: 'e11-w-2/3',
          containerClass: 'xl:e11-w-2/3 e11-mb-4',
          templateSpec: createTemplateSpec('newSwimlaneHeaderTemplate', 'top'),
        }
      ),
    ],
    { containerClass: 'e11-col-span-full' }
  )
}

const createTemplateSpec = (key: string, position: IFormTemplateSpecPosition = 'bottom'): IFormTemplateSpec[] => [
  { templateKey: key, templatePosition: position },
]

export function sendMessageForm(
  defaults: Partial<SendMessageFormDataForBuilder>,
  config: IAutomationFormConfig,
  multimediaArgs?: Partial<MultiMediaArgs>,
  ...extraFormGroups: ReturnType<typeof e11LayoutGroup<any>>[]
): E11FormForType<SendMessageFormDataForBuilder> {
  const { togglePosition = 'top', showStandaloneToggle } = config
  const toggleLayout = sendMessageToggleLayout(defaults, showStandaloneToggle)

  return e11FormFor<SendMessageFormDataForBuilder>()([
    togglePosition === 'top' ? toggleLayout : { fields: [] },
    e11LayoutGroup(
      [
        textMessageForm(defaults, config, multimediaArgs),
        e11LayoutGroup([
          e11LayoutGroup([], { templateSpec: createTemplateSpec('delayTimeHeaderTemplate') }),
          e11LayoutGroup([...delayControlForm(defaults, config), ...extraFormGroups], {
            layoutContainerClass: 'e11-grid e11-grid-cols-1 lg:e11-grid-cols-2 e11-gap-4',
          }),
        ]),
      ],
      { layoutContainerClass: 'e11-grid e11-grid-cols-1 md:e11-grid-cols-2 e11-gap-4' }
    ),
    togglePosition === 'bottom' ? toggleLayout : { fields: [] },
  ])
}

export function sendMessageSwimlaneForm(
  defaults: Partial<SendMessageSwimlaneFormDataForBuilder>,
  config: IAutomationFormConfig,
  swimlaneOptions: ISelectOption<APPLICATION_STATE>[],
  multimediaArgs?: Partial<MultiMediaArgs>
): E11FormForType<SendMessageSwimlaneFormDataForBuilder> {
  const { togglePosition = 'top', showStandaloneToggle } = config
  const toggleLayout = sendMessageToggleLayout(defaults, showStandaloneToggle)

  return e11FormFor<SendMessageSwimlaneFormDataForBuilder>()([
    togglePosition === 'top' ? toggleLayout : { fields: [] },
    e11LayoutGroup(
      [
        textMessageForm(defaults, config, multimediaArgs),
        e11LayoutGroup([
          e11LayoutGroup([], { templateSpec: createTemplateSpec('delayTimeHeaderTemplate') }),
          e11LayoutGroup([...delayControlForm(defaults, config), swimlaneForm(swimlaneOptions, defaults.newSwimlane)], {
            layoutContainerClass: 'e11-grid e11-grid-cols-1 lg:e11-grid-cols-2 e11-gap-4',
          }),
        ]),
      ],
      { layoutContainerClass: 'e11-grid e11-grid-cols-1 md:e11-grid-cols-2 e11-gap-4' }
    ),
    togglePosition === 'bottom' ? toggleLayout : { fields: [] },
  ])
}

export const systemSenderOption: ISelectOption = { name: 'Decision Team', value: MessagingSystemUserIds.hiring_team }

function getMessageControl(
  defaults: Partial<SendMessageFormDataForBuilder>,
  config: IAutomationFormConfig,
  multimediaArgs?: Partial<MultiMediaArgs>
) {
  const { onFileUpload, onFileRemove } = multimediaArgs || {}

  const extractIMarkdownFile = (data: IEditorMessage) => data.files

  const multimediaEventArgs: Partial<MultiMediaMarkdownEditorComponent> = {}
  if (onFileUpload) {
    multimediaEventArgs.onUploadAction = e11FormEventHelper((data: IEditorMessage) => onFileUpload(extractIMarkdownFile(data)))
    multimediaEventArgs.onFileSelection = e11FormEventHelper(onFileUpload)
  }
  if (onFileRemove) {
    multimediaEventArgs.onFileRemoveAction = e11FormEventHelper(onFileRemove)
  }

  if (config.canEditMessages)
    return e11FieldInputDynamicComponent<'message', string, MultiMediaMarkdownEditorComponent>(
      'message',
      'Message',
      MultiMediaMarkdownEditorComponent,
      defaults.message || '',
      {
        extraInputOption: {
          fieldClassList: ['e11-h-24'],
          maxLength: JOBS_AUTOMATION_MULTIMEDIA_CONFIG_OPTIONS.maxCharacterLimit,
          options: JOBS_AUTOMATION_MARKDOWN_CONFIG_OPTIONS,
          maxFileSize: JOBS_AUTOMATION_MULTIMEDIA_CONFIG_OPTIONS.maxFileSize,
          maxNumberOfAttachments: JOBS_AUTOMATION_MULTIMEDIA_CONFIG_OPTIONS.maxNumberOfFiles,
          ...multimediaEventArgs,
        },
        validatorOrOpts: [Validators.required, Validators.maxLength(INPUTS_MAXLENGTH_BY_CONTEXT[InputsContext.LargeText])],
      }
    )
  else
    return e11FieldInputTextArea('message', 'Message', defaults.message || '', {
      extraInputOption: { inputClass: 'e11-h-40' },
      isDisabled: true,
      validatorOrOpts: [Validators.required],
      hideInputError: true, // TODO: review whether this is needed
    })
}
